import { useEffect, useState } from "react";

import { usePrevious } from "../use-previous/usePrevious";

export interface UseDelayedValueProps<T> {
    valueToDelay?: T;
    delayValueInMiliseconds?: number;
    onChange(newValue?: T): void;
    areValuesEqual?(value1?: T, value2?: T): boolean;
}

export const useDelayedValue = <T = string>(props: UseDelayedValueProps<T>) => {
    const prevValue = usePrevious<T | undefined>(props.valueToDelay);
    const [delayedValue, setDelayedValue] = useState<T | undefined>(props.valueToDelay);
    const delayValueInMiliseconds = props.delayValueInMiliseconds ?? 500;

    const handleValuesEqual = (value1?: T, value2?: T): boolean => {
        if (props.areValuesEqual) {
            return props.areValuesEqual(value1, value2);
        }

        return value2 === value1;
    };

    useEffect(() => {
        if (!handleValuesEqual(props.valueToDelay, delayedValue)) {
            setDelayedValue(props.valueToDelay);
        }
    }, [props.valueToDelay]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!handleValuesEqual(delayedValue, prevValue) && !handleValuesEqual(delayedValue, props.valueToDelay)) {
                props.onChange(delayedValue);
            }
        }, delayValueInMiliseconds);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [delayedValue]);

    return { delayedValue, setDelayedValue };
};
